import { useMemo, useState, useEffect } from 'react';
import { ContactDTO } from '@/types';
import { useContacts } from '@/hooks/useContacts';
import { ContactListItem } from './ContactList';
import { X, Search, Loader2, Phone, Mail, Briefcase, ChevronRight } from 'lucide-react';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { useQuery } from '@tanstack/react-query';

import { createPortal } from 'react-dom';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

interface ContactSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (contact: ContactDTO) => void | Promise<void>;
  /** Optional feature-scoped provider for callers that cannot use the general contacts API. */
  searchContacts?: (search: string, page: number) => Promise<{ data: ContactDTO[]; total: number; page?: number; pageSize?: number }>;
  searchScopeKey?: string;
}

export function ContactSearchModal({ isOpen, onClose, onSelect, searchContacts, searchScopeKey }: ContactSearchModalProps) {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const layer = useModalLayer({
    enabled: isOpen,
    onClose,
    historyState: { contactSearchModal: true },
  });

  // Reset search when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchInput('');
      setSearchTerm('');
      setPage(1);
    }
  }, [isOpen]);

  // Debounce search input - update searchTerm after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const standardQuery = useContacts({
    search: searchTerm,
    page,
    enabled: isOpen && !searchContacts,
  });

  const scopedQuery = useQuery({
    queryKey: ['scoped-contact-search', searchScopeKey, searchTerm, page],
    queryFn: () => searchContacts!(searchTerm, page),
    enabled: isOpen && !!searchContacts,
  });

  const data = searchContacts ? scopedQuery.data : standardQuery.data;
  const isLoading = searchContacts ? scopedQuery.isLoading : standardQuery.isLoading;
  const isFetching = searchContacts ? scopedQuery.isFetching : standardQuery.isFetching;

  const contacts = useMemo(() => data?.data ?? [], [data?.data]);
  const total = data?.total ?? contacts.length;
  const pageSize = data?.pageSize ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const listItems: ContactListItem[] = useMemo(
    () =>
      contacts.map((contact) => ({
        id: contact.id,
        contact,
        notes: contact.notes,
      })),
    [contacts]
  );

  const handleSelect = async (item: ContactListItem) => {
    try {
      await onSelect(item.contact);
      onClose();
    } catch {
      // Caller shows the error; keep the picker open.
    }
  };

  // Soft avatar colors, picked from the name so each contact keeps the same one.
  const AVATAR_COLORS = [
    'bg-blue-100 text-blue-700',
    'bg-indigo-100 text-indigo-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-violet-100 text-violet-700',
  ];
  const avatarFor = (name: string) => {
    const trimmed = (name || '').trim();
    const initials = trimmed
      ? trimmed.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('')
      : '?';
    let hash = 0;
    for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
    return { initials, color: AVATAR_COLORS[hash % AVATAR_COLORS.length] };
  };

  function renderContactCards() {
    if (listItems.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white py-8 px-4 text-center text-sm text-gray-500">
          No contacts found.
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {listItems.map((item) => {
          const { contact, notes } = item;
          const { initials, color } = avatarFor(contact.name);
          const jobCount = contact.jobs?.length;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleSelect(item)}
              className="group flex w-full items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-all hover:border-primary-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${color}`}>
                {initials}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-start justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-gray-900">{contact.name || 'No contact name'}</span>
                  {jobCount !== undefined && (
                    <span
                      className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${jobCount > 0 ? 'bg-primary-50 text-primary-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                      <Briefcase className="h-3 w-3" />
                      {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
                    </span>
                  )}
                </span>
                {contact.phone && (
                  <span className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                    <Phone className="h-3.5 w-3.5 flex-shrink-0 text-green-600" />
                    <span className="truncate">
                      {formatPhoneNumber(contact.phone)}
                      {contact.additional_phone ? ` / ${formatPhoneNumber(contact.additional_phone)}` : ''}
                    </span>
                  </span>
                )}
                {contact.email && (
                  <span className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-600">
                    <Mail className="h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
                    <span className="truncate">{contact.email}</span>
                  </span>
                )}
                {notes && <span className="mt-1.5 block text-xs italic text-gray-500 line-clamp-2">{notes}</span>}
              </span>
              <ChevronRight className="mt-2.5 h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-primary-500" />
            </button>
          );
        })}
      </div>
    );
  }

  if (!isOpen) return null;

  return createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
    <div className="fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4" style={layer.style}>
      <div className="flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">Select Contact</h3>
          <button
            type="button"
            onClick={layer.onClose}
            className="rounded-full p-1 text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-100 px-6 py-4 flex-shrink-0">
          <div className="flex w-full items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts by name, phone, or email"
              className="flex-1 text-sm text-gray-700 focus:outline-none"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-4 sm:px-6 min-h-0">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center text-gray-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading contacts...
            </div>
          ) : (
            renderContactCards()
          )}
          {isFetching && !isLoading && (
            <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating results...
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 text-xs text-gray-500 flex-shrink-0">
          <span>
            Showing {contacts.length} of {total} contacts
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    </LayerDepthContext.Provider>,
    document.body
  );
}

